import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  List,
  ListItem,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { db } from '../../firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import dayjs from 'dayjs';

const Orders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterType = searchParams.get("filterType") || "year";
  const companyCode = searchParams.get('code');
  const [orders, setOrders] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedUnitCode, setSelectedUnitCode] = useState('all');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUnits = async () => {
    try {
      const unitsQuery = query(collection(db, 'units'), where('companyCode', '==', companyCode));
      const unitsSnapshot = await getDocs(unitsQuery);
      const unitList = unitsSnapshot.docs.map(doc => ({
        unitCode: doc.id,
        name: doc.data().name || doc.id,
      }));
      setUnits(unitList);
      console.log('Orders: Fetched units:', unitList);
      if (unitList.length === 0) {
        console.warn('Orders: No units found for companyCode:', companyCode);
      }
    } catch (err) {
      console.error('Orders: Error fetching units:', err);
      setError('Failed to load units.');
    }
  };

  const fetchOrders = async () => {
    try {
      if (!companyCode) {
        setError('No company code provided. Please specify a company code in the URL (e.g., ?code=COMPANY1).');
        setLoading(false);
        return;
      }

      console.log('Orders: Fetching orders for company:', companyCode, 'unit:', selectedUnitCode);
      let ordersQuery;
      if (selectedUnitCode === 'all') {
        ordersQuery = query(
          collection(db, 'orders'),
          where('companyCode', '==', companyCode),
          orderBy('createdAt', 'desc')
        );
      } else {
        ordersQuery = query(
          collection(db, 'orders'),
          where('companyCode', '==', companyCode),
          where('unitCode', '==', selectedUnitCode),
          orderBy('createdAt', 'desc')
        );
      }
      const ordersSnapshot = await getDocs(ordersQuery);
      /*const ordersList = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));*/

      let ordersList = ordersSnapshot.docs.map(doc => ({
  id: doc.id,
  ...doc.data(),
}));

// Apply date filters
const now = dayjs();

if (filterType === "today") {
  ordersList = ordersList.filter(order => {
    if (!order.createdAt?.toDate) return false;

    const orderDate = dayjs(order.createdAt.toDate());

    return orderDate.isSame(now, "day");
  });
}

else if (filterType === "month") {
  ordersList = ordersList.filter(order => {
    if (!order.createdAt?.toDate) return false;

    const orderDate = dayjs(order.createdAt.toDate());

    return (
      orderDate.month() === now.month() &&
      orderDate.year() === now.year()
    );
  });
}

else if (filterType === "year") {
  ordersList = ordersList.filter(order => {
    if (!order.createdAt?.toDate) return false;

    const orderDate = dayjs(order.createdAt.toDate());

    return orderDate.year() === now.year();
  });
}
      console.log('Orders: Fetched orders:', ordersList);
      setOrders(ordersList);
      setLoading(false);
    } catch (err) {
      console.error('Orders: Error fetching orders:', err, { code: err.code, message: err.message });
      // setError(`Failed to load orders: ${err.message}`);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyCode) {
      fetchUnits();
      fetchOrders();
    }
  }, [companyCode, selectedUnitCode]);

  const groupOrdersByDate = (orders) => {
    const grouped = {};
    orders.forEach((order) => {
      if (order.createdAt) {
        const date = order.createdAt.toDate
          ? order.createdAt.toDate().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : 'Unknown Date';
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(order);
      }
    });
    return grouped;
  };

  const groupedOrders = groupOrdersByDate(orders);

  const handleTabChange = (event, newValue) => {
    setSelectedUnitCode(newValue);
    setLoading(true);
    setError(null);
    console.log('Orders: Selected unitCode:', newValue);
  };

  const handleBack = () => {
    console.log('Orders: Navigating to /admin/home');
    navigate('/admin/home');
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/*<Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Order History for Company {companyCode || 'N/A'}
      </Typography>*/}
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
              Order History (
              {filterType === "today"
                ? "Today"
                : filterType === "month"
                ? "Month"
                : "Year"}
              )
            </Typography>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={selectedUnitCode}
          onChange={handleTabChange}
          aria-label="unit filter tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All Units" value="all" />
          {units.map(unit => (
            <Tab key={unit.unitCode} label={unit.name} value={unit.unitCode} />
          ))}
        </Tabs>
      </Box>

      {error && (
        <Typography variant="h6" color="error" sx={{ textAlign: 'center', mb: 3 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
          Loading orders...
        </Typography>
      ) : (
        <Box>
          {Object.keys(groupedOrders).length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {selectedUnitCode === 'all'
                ? 'No orders in all units.'
                : `No orders in the selected unit (${units.find(u => u.unitCode === selectedUnitCode)?.name || selectedUnitCode}).`}
            </Typography>
          ) : (
            Object.keys(groupedOrders).map((date) => (
              <Box key={date} sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {date}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {groupedOrders[date].map((order, index) => (
                    <React.Fragment key={order.id}>
                      <ListItem
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          py: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Typography variant="h6">
                            Table ID: {order.tableId}
                          </Typography>
                          <Typography variant="body1" color={order.status === 'Delivered' ? 'success.main' : 'text.primary'}>
                            Status: {order.status}
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          Order ID: {order.orderId}
                        </Typography>
                        <Typography variant="body1">
                          Unit: {units.find(u => u.unitCode === order.unitCode)?.name || 'Unknown Unit'}
                        </Typography>
                        <Typography variant="body1">
                          Time: {order.createdAt.toDate
                            ? order.createdAt.toDate().toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </Typography>
                        <Typography variant="body1">
                          Total Amount: ₹{order.grandTotal || order.totalAmount || 0}
                        </Typography>
                        <Divider sx={{ my: 1, width: '100%' }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Items:
                        </Typography>
                        {order.items.map((item, itemIndex) => (
                          <Typography key={itemIndex} variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                            {item.name || 'Unknown Item'} (x{item.quantity ?? 1}) - ₹{item.totalPrice || (item.quantity ?? 1) * (item.price ?? 0)} - Status: {item.status || 'Pending'}
                          </Typography>
                        ))}
                      </ListItem>
                      {index < groupedOrders[date].length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ))
          )}
        </Box>
      )}

      <Button variant="contained" color="primary" onClick={handleBack} sx={{ mt: 3 }}>
        Back to Home
      </Button>
    </Container>
  );
};

export default Orders;